"use client";
import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  ChevronLeft, ChevronRight, Plus, X, Eye,
  Clock, Coffee, ListChecks, AlarmClock,
  Users, ShieldCheck, Filter, Palmtree,
  ChevronDown, Code, Palette, Briefcase, Check, ArrowLeft,
  CheckCircle2, Star, AlertCircle, PlaneTakeoff, CalendarOff, Pencil,
} from "lucide-react";
import QRCode from "qrcode";
import { useCalendar } from "../../context/CalendarContext";
import { useEmployee } from "../../context/EmployeeContext";
import { useProjects } from "../../context/ProjectContext";
import { useTimesheets } from "../../context/TimesheetContext";
import { useAttendance } from "../../context/AttendanceContext";
import { BASE_URL, QR_LINK } from "../../config/apiConfig";

// ---------------------------------------------------------------------------
// Constants that remain static (layout / styling only — NOT data)
// ---------------------------------------------------------------------------

const GRID_START = 0;               // 12:00 AM in minutes — full 24h grid so overtime blocks are never clipped
const PX_PER_MIN = 1;               // 1px per minute
const GRID_HEIGHT = 24 * 60;        // 24 hours x 60px = 1440 (full day)
const VISIBLE_GRID_HEIGHT = 600;    // visible viewport height before scrolling kicks in
const DEFAULT_SCROLL_HOUR = 8;      // grid auto-scrolls to 8 AM on open so the normal workday is visible first
const HOURS = Array.from({ length: 24 }, (_, i) => i); // 12 AM ... 11 PM labels

function hourLabel(h) {
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const STATUSES = ["Pending", "Approved", "Rejected", "In Progress"];

const BLOCK_STYLES = {
  checkin:  { bg: "#dbeafe", border: "#bfdbfe", text: "#1e40af" },
  checkout: { bg: "#dcfce7", border: "#bbf7d0", text: "#166534" },
  break:    { bg: "#ede9fe", border: "#ddd6fe", text: "#5b21b6" },
  work:     { bg: "#f1f5f9", border: "#cbd5e1", text: "#334155" },
  meeting:  { bg: "#ffedd5", border: "#fed7aa", text: "#9a3412" },
};

const STATUS_META = {
  present: { Icon: CheckCircle2, color: "#16a34a", bg: "#dcfce7", label: "Present"  },
  half:    { Icon: Star,         color: "#ca8a04", bg: "#fef9c3", label: "Half Day"  },
  late:    { Icon: AlertCircle,  color: "#7c3aed", bg: "#f3e8ff", label: "Late"      },
  leave:   { Icon: PlaneTakeoff, color: "#0284c7", bg: "#e0f2fe", label: "Leave"     },
  holiday: { Icon: CalendarOff,  color: "#dc2626", bg: "#fee2e2", label: "Holiday"   },
};

const LEGEND = [
  { key: "checkin",  color: "#2563eb", label: "Check-In / Check-Out" },
  { key: "work",     color: "#d97706", label: "Work / Timesheet" },
  { key: "break",    color: "#7c3aed", label: "Break" },
  { key: "meeting",  color: "#ea580c", label: "Meeting" },
  { key: "leave",    color: "#dc2626", label: "Leave" },
  { key: "late",     color: "#7c3aed", label: "Late" },
  { key: "half",     color: "#0284c7", label: "Half Day" },
  { key: "present",  color: "#16a34a", label: "Present" },
];

// ---------------------------------------------------------------------------
// Date helpers (pure functions — no data dependency, unchanged)
// ---------------------------------------------------------------------------

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function weekdayIndex(date) {
  return (date.getDay() + 6) % 7;
}
function fmtRange(start, end) {
  const s = `${MONTH_NAMES[start.getMonth()].slice(0, 3)} ${start.getDate()}`;
  const e = `${MONTH_NAMES[end.getMonth()].slice(0, 3)} ${end.getDate()}, ${end.getFullYear()}`;
  return `${s} - ${e}`;
}

// KEY FIX: toTop uses PX_PER_MIN so positions match the scaled grid
function toTop(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  return (h * 60 + m - GRID_START) * PX_PER_MIN;
}

// Raised the minimum block height to 40px so single check-in/out and short
// meeting blocks always have enough room for both the title and time line
// to render without being clipped or crammed against the block border.
function toHeight(start, end) {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  return Math.max(((eh * 60 + em) - (sh * 60 + sm)) * PX_PER_MIN - 3, 40);
}

function initials(name) {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}
function to12h(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}
function diffLabel(start, end) {
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  const mins = Math.max((eh * 60 + em) - (sh * 60 + sm), 0);
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
}
function calcWorkedHours(startTime, endTime) {
  if (!startTime || !endTime) return "";
  const diff = (new Date(`1970-01-01T${endTime}`) - new Date(`1970-01-01T${startTime}`)) / 60000;
  if (diff <= 0) return "";
  return `${Math.floor(diff / 60)}h ${String(diff % 60).padStart(2, "0")}m`;
}

// ---------------------------------------------------------------------------
// Attendance flow helpers (QR + checkout-mail) — shared by CalendarView and
// PersonalCalendar via the useAttendanceFlow hook below.
// ---------------------------------------------------------------------------

function formatDateDDMMYYYY(date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

/**
 * Encapsulates the "Mark Attendance" QR modal + checkout-mail modal flow,
 * mirroring AttendancePage's handleAttendanceFlow / generateAttendanceQR /
 * handleManualAttendance / handleMailSubmit. Reused by both CalendarView and
 * PersonalCalendar so the "Add Attendance" button behaves identically to the
 * "Mark Your Attendance" button on the Attendance page.
 */
function useAttendanceFlow() {
  const { attendanceData, fetchMonthlyAttendance, addManualAttendance, sendAttendanceMail } = useAttendance();

  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCode, setQrCode]           = useState("");
  const [loadingQR, setLoadingQR]     = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const [showMailModal, setShowMailModal] = useState(false);
  const [mailText, setMailText]           = useState("");
  const [mailDate, setMailDate]           = useState("");
  const [mailLoading, setMailLoading]     = useState(false);

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!showQrModal) return;
    generateAttendanceQR();
    const interval = setInterval(generateAttendanceQR, 120_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQrModal]);

  const generateAttendanceQR = async () => {
    try {
      setLoadingQR(true);
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${BASE_URL}/qr/generate/${user.id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await res.json();

      if (data?.token) {
        const qrUrl = `${QR_LINK}?qr_session=${data.token}&session_id=${token}`;
        const qrImage = await QRCode.toDataURL(qrUrl);
        setQrCode(qrImage);
      }
    } catch (err) {
      console.error("QR Error:", err);
    } finally {
      setLoadingQR(false);
    }
  };

  // Entry point — call this from the "Add Attendance" button.
  const startAttendanceFlow = async (selectedMonth, selectedYear) => {
    const now = new Date();
    const month = selectedMonth ?? now.getMonth();
    const year  = selectedYear ?? now.getFullYear();

    const freshAttendanceData = await fetchMonthlyAttendance(month, year);

    const todayStr = formatDateDDMMYYYY(now);
    const emp = (freshAttendanceData || attendanceData).find((e) => e.user_id === user.id);
    const shouldShowMailModal = Number(emp?.show_mail_modal ?? 0);

    if (shouldShowMailModal === 1) {
      setMailDate(todayStr);
      setShowMailModal(true);
    } else {
      setShowQrModal(true);
      generateAttendanceQR();
    }
  };

  const handleManualAttendance = async () => {
    if (!user?.id) return alert("User not found");

    const payload = {
      user_id: user.id,
      attendance_date: formatDateDDMMYYYY(new Date()),
      latitude: null,
      longitude: null,
      image: null,
      create_audit_id: user.id,
    };

    try {
      const res = await addManualAttendance(payload);
      const { status, message } = res?.data || {};
      if (status === 200 || status === 201) {
        alert(message);
        setShowQrModal(false);
      } else {
        alert(message || "Failed to mark attendance");
      }
    } catch {
      alert("Failed to mark attendance");
    }
  };

  const handleMailSubmit = async () => {
    setMailLoading(true);
    const payload = { user_id: user.id, attendance_date: mailDate, message: mailText };

    try {
      const res = await sendAttendanceMail(payload);
      if (res?.success) {
        setShowMailModal(false);
        setMailText("");
        setShowQrModal(true);
        generateAttendanceQR();
      } else {
        alert(res?.data?.message || "Mail failed");
      }
    } catch {
      alert("Something went wrong");
    } finally {
      setMailLoading(false);
    }
  };

  return {
    // QR modal
    showQrModal, setShowQrModal, qrCode, loadingQR, currentTime, handleManualAttendance,
    // Mail modal
    showMailModal, setShowMailModal, mailText, setMailText, mailDate, mailLoading, handleMailSubmit,
    // Entry point
    startAttendanceFlow,
  };
}

/**
 * Renders the QR "Mark Attendance" modal + the checkout "Mail" modal.
 * Drop this once per component that calls useAttendanceFlow().
 */
function AttendanceFlowModals({ flow }) {
  return (
    <>
      {flow.showQrModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl w-[380px] shadow-xl animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Mark Attendance</h2>
              <button onClick={() => flow.setShowQrModal(false)} className="text-gray-600 hover:text-black text-xl">×</button>
            </div>

            <div className="flex justify-center mb-4">
              {flow.loadingQR ? (
                <div className="text-center text-gray-500">Generating QR...</div>
              ) : flow.qrCode ? (
                <img src={flow.qrCode} className="w-48 h-48 object-contain" alt="QR Code" />
              ) : (
                <div className="text-gray-400">No QR generated</div>
              )}
            </div>

            <div className="text-center text-gray-700 mb-4">
              <p className="text-lg font-medium">{flow.currentTime.toLocaleDateString()}</p>
              <p className="text-xl font-bold">{flow.currentTime.toLocaleTimeString()}</p>
            </div>

            <button onClick={flow.handleManualAttendance} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg">
              Mark Attendance Manually
            </button>
          </div>
        </div>
      )}

      {flow.showMailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl w-[380px] shadow-xl animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Checkout Mail Required</h2>
              <button onClick={() => flow.setShowMailModal(false)} className="text-gray-600 hover:text-black text-xl">×</button>
            </div>

            <div className="text-center text-gray-700 mb-4">
              <p className="text-lg font-medium">Date: {flow.mailDate}</p>
            </div>

            <textarea
              className="w-full border p-3 rounded-lg mb-5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your daily report message..."
              rows={5}
              value={flow.mailText}
              onChange={(e) => flow.setMailText(e.target.value)}
            />

            <div className="flex gap-3">
              <button onClick={() => flow.setShowMailModal(false)} className="w-1/2 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-100">
                Close
              </button>
              <button onClick={flow.handleMailSubmit} disabled={flow.mailLoading} className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg disabled:opacity-50">
                {flow.mailLoading ? "Sending..." : "Send Mail"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Derived per-day data for the sidebar
// ---------------------------------------------------------------------------
// CHANGED: this used to call blocksFor(date) / dayStatusFor(date) against
// hardcoded mocks. It now takes the already-resolved `blocks` and `status`
// for one user/day (pulled from CalendarContext by the caller) so this
// function stays a pure formatter, not a data source.

function deriveDayData(date, blocks, status, isWeekend) {
  // A weekend day can still have real check-in/timesheet blocks (e.g. the
  // employee logged work on a Saturday/Sunday). In that case it should be
  // treated as a normal working day with real data instead of being blanked
  // out as "Weekend — no records", which was hiding actually-worked hours.
  isWeekend = isWeekend && (!Array.isArray(blocks) || blocks.length === 0);

  const isLeave   = status === "leave";
  const isHoliday = status === "holiday";

  const checkinBlock  = blocks.find(b => b.type === "checkin");
  const checkoutBlock = blocks.find(b => b.type === "checkout");
  const breakBlocks   = blocks.filter(b => b.type === "break");
  const workBlocks    = blocks.filter(b => b.type === "work" || b.type === "meeting");

  let totalWorkMins = 0;
  let totalBreakMins = 0;
  let meetingMins = 0;
  blocks.forEach(b => {
    const [sh, sm] = b.start.split(":").map(Number);
    const [eh, em] = b.end.split(":").map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (b.type === "work")    { totalWorkMins += mins; }
    if (b.type === "meeting") { totalWorkMins += mins; meetingMins += mins; }
    if (b.type === "break")   { totalBreakMins += mins; }
  });

  const fmtMins = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}h ${String(mins % 60).padStart(2, "0")}m`;
  const graceTotal = 100;
  const graceUsed  = status === "late" ? 25 : 0;

  const punches = isWeekend || isLeave || isHoliday ? [] : [
    ...(checkinBlock  ? [{ key: "checkin",  label: "Check-In",   value: checkinBlock.sub  }] : []),
    ...breakBlocks.flatMap(b => ([
      { key: "lunchin",  label: "Lunch In",  value: to12h(b.start) },
      { key: "lunchout", label: "Lunch Out", value: to12h(b.end)   },
    ])),
    ...(checkoutBlock ? [{ key: "checkout", label: "Check-Out", value: checkoutBlock.sub }] : []),
  ];

  const summary = isWeekend
    ? [
        { icon: "hours",  label: "Total Work Hours", value: "00h 00m"         },
        { icon: "break",  label: "Break Time",       value: "00h 00m"         },
        { icon: "grace",  label: "Grace Period",     value: `${graceTotal} mins` },
        { icon: "used",   label: "Grace Used",       value: "0 mins"          },
        { icon: "status", label: "Status",           value: "Weekend"         },
      ]
    : isHoliday
    ? [
        { icon: "hours",  label: "Total Work Hours", value: "00h 00m"         },
        { icon: "break",  label: "Break Time",       value: "00h 00m"         },
        { icon: "grace",  label: "Grace Period",     value: `${graceTotal} mins` },
        { icon: "used",   label: "Grace Used",       value: "0 mins"          },
        { icon: "status", label: "Status",           value: "Holiday", cls: "text-red-600" },
      ]
    : isLeave
    ? [
        { icon: "hours",  label: "Total Work Hours", value: "00h 00m"         },
        { icon: "break",  label: "Break Time",       value: "00h 00m"         },
        { icon: "grace",  label: "Grace Period",     value: `${graceTotal} mins` },
        { icon: "used",   label: "Grace Used",       value: "0 mins"          },
        { icon: "status", label: "Status",           value: "On Leave", cls: "text-sky-600" },
      ]
    : [
        { icon: "hours",  label: "Total Work Hours", value: fmtMins(totalWorkMins)  },
        { icon: "break",  label: "Break Time",       value: fmtMins(totalBreakMins) },
        { icon: "grace",  label: "Grace Period",     value: `${graceTotal} mins`    },
        { icon: "used",   label: "Grace Used",       value: `${graceUsed} mins`, cls: graceUsed ? "text-orange-500" : undefined },
        { icon: "status", label: "Status",           value: status === "late" ? "Late (25 mins)" : "On Time", cls: status === "late" ? "text-purple-600" : "text-green-600" },
      ];

  const timesheet = (isWeekend || isLeave || isHoliday) ? [] : workBlocks.map(b => ({
    color: b.type === "meeting" ? "#ea580c" : "#d97706",
    name:  b.type === "meeting" ? `${b.label} (Meeting)` : b.label,
    time:  `${to12h(b.start)} - ${to12h(b.end)}`,
    hrs:   diffLabel(b.start, b.end),
    // Only entries backed by a real timesheet record (see `raw` on the
    // synthetic work block in useMergedBlocksFor) carry `raw` — that's what
    // gates showing the per-entry edit button below.
    raw:   b.raw || null,
  }));

  const footer = (isWeekend || isLeave || isHoliday) ? [
    { label: "Total Work Hours", value: "00h 00m" },
    { label: "Total Break Time", value: "00h 00m" },
    { label: "Total Meetings",   value: "00h 00m" },
    { label: "Timesheets Added", value: "0"       },
    { label: "Productive Hours", value: "00h 00m" },
    { label: "Efficiency",       value: "-"       },
  ] : [
    { label: "Total Work Hours", value: fmtMins(totalWorkMins)  },
    { label: "Total Break Time", value: fmtMins(totalBreakMins) },
    { label: "Total Meetings",   value: fmtMins(meetingMins)    },
    { label: "Timesheets Added", value: String(timesheet.length) },
    { label: "Productive Hours", value: fmtMins(totalWorkMins - meetingMins) },
    { label: "Efficiency",       value: totalWorkMins > 0 ? `${Math.round(((totalWorkMins - meetingMins) / totalWorkMins) * 100)}%` : "-" },
  ];

  const daySummary = {
    lateArrival: status === "late" ? "25 mins" : "0 mins",
    halfDay:     status === "half" ? "Yes" : "No",
    efficiency:  footer.find(f => f.label === "Efficiency")?.value || "-",
    productive:  footer.find(f => f.label === "Total Work Hours")?.value || "00h 00m",
  };

  return { summary, punches, timesheet, footer, daySummary, isLeave, isHoliday, weekend: isWeekend, status };
}

// ---------------------------------------------------------------------------
// Status Icon + Badge
// ---------------------------------------------------------------------------

function StatusBadge({ status, size = "sm" }) {
  const meta = STATUS_META[status] || { bg: "#f3f4f6", color: "#374151", label: status, Icon: null };
  const fs       = size === "xs" ? 9  : size === "sm" ? 11 : 12;
  const iconSize = size === "xs" ? 10 : size === "sm" ? 11 : 13;
  return (
    <span className="inline-flex items-center gap-1"
      style={{ background: meta.bg, color: meta.color, fontSize: fs, fontWeight: 600, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>
      {meta.Icon && <meta.Icon size={iconSize} strokeWidth={2.4} />}
      {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Hour grid lines  -- spaced by PX_PER_MIN * 60
// ---------------------------------------------------------------------------

function HourLines() {
  return (
    <>
      {HOURS.map((_, i) => (
        <div key={i} className="absolute left-0 right-0 border-t border-gray-100"
          style={{ top: i * 60 * PX_PER_MIN }} />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Attendance Images Modal
// ---------------------------------------------------------------------------
// CHANGED: now takes `todayImages` (from CalendarContext.todayImagesFor) and
// `graceInfo` instead of hardcoded placeholder times. Falls back gracefully
// when a particular punch/image hasn't happened yet today.

// The backend sends these face-capture images as raw base64 (no data: URI
// prefix) — the same field shape the Attendance page's table already
// prefixes correctly (see AttendancePage.jsx's `data:image/png;base64,...`).
// This modal was rendering the raw string directly as <img src>, which the
// browser can't decode, so every captured face photo appeared blank/broken.
function toImageSrc(raw) {
  if (!raw) return null;
  if (raw.startsWith("data:") || raw.startsWith("http")) return raw;
  return `data:image/png;base64,${raw}`;
}

function AttendanceModal({ onClose, personName, todayImages, graceInfo }) {
  const images = [
    { key: "check_in",  label: "Check In",  img: toImageSrc(todayImages?.check_in_image),  time: todayImages?.check_in_time  },
    { key: "lunch_in",  label: "Lunch In",  img: toImageSrc(todayImages?.lunch_in_image),  time: todayImages?.lunch_in_time  },
    { key: "lunch_out", label: "Lunch Out", img: toImageSrc(todayImages?.lunch_out_image), time: todayImages?.lunch_out_time },
    { key: "check_out", label: "Check Out", img: toImageSrc(todayImages?.check_out_image), time: todayImages?.check_out_time },
  ];

  const grace = graceInfo || { allowed: 100, used: 0, status: "On Time" };
  const remaining = Math.max(grace.allowed - grace.used, 0);

  const details = [
    { Icon: ShieldCheck, k: "Grace Period (Allowed)", v: `${grace.allowed} mins` },
    { Icon: ShieldCheck, k: "Grace Used",             v: `${grace.used} mins`,    vc: grace.used ? "text-orange-500" : undefined },
    { Icon: ShieldCheck, k: "Grace Remaining",        v: `${remaining} mins`,     vc: "text-green-600" },
    { Icon: AlarmClock,  k: "Status",                 v: grace.status,           vc: grace.used ? "text-purple-600" : "text-green-600" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-[460px] relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"><X size={18} /></button>
        <h2 className="text-base font-semibold text-gray-800 mb-4">Attendance Images - {personName || "Employee"}</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          {images.map(({ key, label, img, time }) => (
            <div key={key} className="flex flex-col items-center gap-1.5">
              <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-3xl border border-gray-100 overflow-hidden">
                {img ? (
                  <img src={img} alt={label} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-gray-300 text-sm">--</span>
                )}
              </div>
              <p className="text-sm font-medium text-gray-800">{label}</p>
              <p className="text-xs text-gray-400">{time ? to12h(time.slice(0, 5)) : "Not recorded"}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-100 pt-3 space-y-2">
          {details.map(({ Icon, k, v, vc }) => (
            <div key={k} className="flex justify-between items-center text-sm">
              <span className="text-gray-400 flex items-center gap-1.5"><Icon size={13} /> {k}</span>
              <span className={`font-medium ${vc || "text-gray-800"}`}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Add Timesheet Modal
// ---------------------------------------------------------------------------
// CHANGED: employee dropdown now reads from CalendarContext.employees
// instead of getEmployeeOptions(CURRENT_USER). Submit now POSTs to the
// backend and calls refreshCalendar() on success instead of being a no-op.
//
// FIXED (this pass):
//   1) The read-only "Employee Name" field for a logged-in employee was
//      reading a non-existent `.name` property (employee records only have
//      `first_name` / `last_name`, as used a few lines below in the
//      manager/admin dropdown's <option> labels) and compared ids without
//      normalizing to string, so it could silently render blank. It now
//      resolves the current employee's record from `employeeList` by
//      matching `empId`/`id` as strings, and builds the display name from
//      `first_name` + `last_name`.
//   2) The Status dropdown used to always show every value in `STATUSES`
//      regardless of role. Employees and Managers can now only ever submit
//      with status "Pending" — the dropdown is restricted to that single
//      option and disabled for those roles so it can't be changed
//      client-side. Every other role (Admin, HR, QA, ...) still gets the
//      full set of selectable statuses: Pending, Approved, Rejected.

function QuickAddModal({ onClose, defaultDate, presetEmployeeId, currentUserId, isManager, userRole }) {
  const { employees } = useEmployee();
  const employeeList = Array.isArray(employees) ? employees : [];
  const { projects } = useProjects();
  const { createTimesheet } = useTimesheets();

  const canSelectEmployee =
    isManager ||
    userRole === "Admin" ||
    userRole === "QA";

  // Resolve the logged-in employee's own record (by empId or id, compared
  // as strings so numeric/string id mismatches don't cause a blank name).
  const currentEmployee = employeeList.find(
    (e) => String(e.empId ?? e.id) === String(currentUserId)
  );
  const currentEmployeeName = currentEmployee
    ? `${currentEmployee.first_name ?? ""} ${currentEmployee.last_name ?? ""}`.trim()
    : "";

  // Employees and Managers can only ever submit as "Pending". Every other
  // role (Admin, HR, QA, ...) can pick from Pending/Approved/Rejected.
  const canSelectStatus = !isManager && userRole?.toLowerCase() !== "employee";
  const allowedStatuses = canSelectStatus ? ["Pending", "Approved", "Rejected"] : ["Pending"];

  const [employeeId, setEmployeeId] = useState(
    presetEmployeeId
      ? String(presetEmployeeId)
      : (isManager || userRole === "Admin" || userRole === "QA"
          ? ""
          : String(currentUserId))
  );

  const [project,   setProject]   = useState("");
  const [date,      setDate]      = useState(defaultDate || "");
  const [startTime, setStartTime] = useState("");
  const [endTime,   setEndTime]   = useState("");
  const [overtime,  setOvertime]  = useState("");
  const [status,    setStatus]    = useState("Pending");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const workedHours = useMemo(() => calcWorkedHours(startTime, endTime), [startTime, endTime]);

  const labelCls    = "block text-sm font-medium text-gray-700 mb-1";
  const inputCls    = "border border-gray-300 rounded-lg px-3 py-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white";
  const readonlyCls = "border border-gray-200 rounded-lg px-3 py-2 w-full text-sm bg-gray-50 text-gray-500 cursor-not-allowed";

  const handleSubmit = async (e) => {
    e.preventDefault();

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const payload = {
      emp_id: String(employeeId),
      project_id: String(project),
      date: new Date(date)
        .toLocaleDateString("en-GB")
        .replace(/\//g, "-"),   // ✅ DD-MM-YYYY format

      check_in: startTime,
      check_out: endTime,

      working_hours: String(workedHours || "0"),
      overtime: String(overtime || "0"),
      // Employees and Managers can only ever submit "Pending"; guard here
      // too in case client state was somehow manipulated, in addition to
      // the disabled dropdown below.
      status: canSelectStatus ? (status || "Pending") : "Pending",

      create_audit_id: String(user?.id || ""),
    };

    if (
      !payload.emp_id ||
      !payload.project_id ||
      !payload.date ||
      !payload.check_in ||
      !payload.check_out
    ) {
      setSubmitError("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      await createTimesheet(payload);
      onClose();
    } catch (err) {
      console.error("❌ Failed:", err);
      setSubmitError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[600px] relative overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Add Timesheets</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {submitError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Employee Name</label>
              {!canSelectEmployee ? (
                <input
                  type="text"
                  readOnly
                  value={currentEmployeeName}
                  className={readonlyCls}
                />
              ) : (
                <div className="relative">
                  <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} className={inputCls + " appearance-none pr-8"}>
                    <option value="">Select Employee</option>
                    {employeeList
                      .filter(emp => emp.status?.toString().trim().toLowerCase() === "active")
                      .map(emp => (
                        <option key={emp.id} value={String(emp.empId || emp.id)}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>Project Name</label>
              <div className="relative">
                <select
                  value={project}
                  onChange={e => setProject(e.target.value)}
                  className={inputCls + " appearance-none pr-8"}
                >
                  <option value="">Select Project</option>

                  {Array.isArray(projects) && projects.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.project_name}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls} />
            </div>
            <div className="hidden sm:block">
              <label className={labelCls}>Worked Hours</label>
              <input type="text" readOnly value={workedHours} placeholder="Auto-calculated" className={readonlyCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Start Time</label>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End Time</label>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className={inputCls} />
            </div>
          </div>
          {workedHours && (
            <p className="sm:hidden text-xs text-gray-400 -mt-1">Worked hours: <span className="font-medium text-gray-600">{workedHours}</span></p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="hidden sm:block">
              <label className={labelCls}>Overtime</label>
              <input type="text" value={overtime} onChange={e => setOvertime(e.target.value)} placeholder="e.g., 1.5 hrs" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <div className="relative">
                <select
                  value={canSelectStatus ? status : "Pending"}
                  onChange={e => setStatus(e.target.value)}
                  disabled={!canSelectStatus}
                  className={inputCls + " appearance-none pr-8" + (!canSelectStatus ? " bg-gray-50 text-gray-500 cursor-not-allowed" : "")}
                >
                  {allowedStatuses.map(s => (<option key={s} value={s}>{s}</option>))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} disabled={submitting} className="border border-gray-300 px-5 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} className="text-white px-6 py-2 rounded-lg text-sm font-semibold shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#2563eb,#d12465)" }}>
            {submitting ? "Adding..." : "Add Timesheet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filters Dropdown
// ---------------------------------------------------------------------------

function FiltersDropdown({ filters, setFilters }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle     = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const activeCount = LEGEND.filter(l => !filters[l.key]).length;
  const allOn      = LEGEND.every(l => filters[l.key]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-colors ${
          activeCount > 0 ? "border-purple-300 bg-purple-50 text-purple-700" : "border-gray-300 hover:bg-gray-50 text-gray-700"
        }`}>
        <Filter size={13} /> <span className="hidden sm:inline">Filters</span>
        {activeCount > 0 && (
          <span className="ml-0.5 text-[10px] bg-purple-600 text-white rounded-full w-4 h-4 flex items-center justify-center">{activeCount}</span>
        )}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-[220px] py-2">
          <div className="flex items-center justify-between px-3 pb-2 mb-1 border-b border-gray-50">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Show types</span>
            <button onClick={() => setFilters(Object.fromEntries(LEGEND.map(l => [l.key, !allOn])))}
              className="text-xs text-purple-600 hover:text-purple-800 font-medium">
              {allOn ? "Clear all" : "Select all"}
            </button>
          </div>
          {LEGEND.map(({ key, color, label }) => (
            <button key={key} onClick={() => toggle(key)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors text-left">
              <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border"
                style={{ background: filters[key] ? color : "transparent", borderColor: color }}>
                {filters[key] && <Check size={11} className="text-white" />}
              </span>
              <span className="text-gray-700">{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legend bar
// ---------------------------------------------------------------------------

function LegendBar({ filters, setFilters, onAddAttendance }) {
  const blockLegend  = LEGEND.filter(l => ["checkin", "work", "break", "meeting"].includes(l.key));
  const statusLegend = [
    { key: "present", status: "present" },
    { key: "half",    status: "half"    },
    { key: "late",    status: "late"    },
    { key: "leave",   status: "leave"   },
  ];
  return (
    <div className="bg-white rounded-xl shadow-sm px-4 py-2.5 mb-4 flex flex-wrap items-center gap-x-5 gap-y-2">
      {blockLegend.map(({ key, color, label }) => (
        <button
          key={key}
          onClick={() => setFilters(prev => ({ ...prev, [key]: !prev[key] }))}
          className={`hidden sm:flex items-center gap-1.5 text-xs transition-opacity ${
            filters[key] ? "text-gray-500" : "text-gray-300 opacity-50"
          }`}
        >
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: color }}
          />
          {label}
        </button>
      ))}

      <span className="hidden sm:inline w-px h-3.5 bg-gray-200" />

      {statusLegend.map(({ key, status }) => {
        const meta = STATUS_META[status];
        return (
          <button
            key={key}
            onClick={() => setFilters(prev => ({ ...prev, [key]: !prev[key] }))}
            className={`flex items-center gap-1.5 text-xs font-medium transition-opacity ${
              filters[key] ? "" : "opacity-40"
            }`}
            style={{ color: filters[key] ? meta.color : "#9ca3af" }}
          >
            <meta.Icon size={13} strokeWidth={2.4} />
            <span className="hidden sm:inline">{meta.label}</span>
          </button>
        );
      })}

      <button
        onClick={onAddAttendance}
        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white  shadow-sm transition-all"
        style={{ background: "linear-gradient(135deg,#2563eb,#d12465)" }}>
        + Add Attendance
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Merge Timesheet entries into attendance blocks
// ---------------------------------------------------------------------------
// FIXED: the attendance calendar's per-day `blocks` (from CalendarContext /
// the `/attendance-calendar` API) only ever contain check-in/check-out/break
// blocks — they never included entries logged through the separate
// Timesheets feature (`/timesheets`, TimesheetContext). So a timesheet entry
// submitted for a day (e.g. a Saturday with no attendance check-in) never
// showed up anywhere on this page: not as a "Work / Timesheet" block in the
// grid, not in the sidebar's Timesheet Entries card, and the day kept
// rendering as a blank Weekend/Absent day. This hook merges matching
// timesheet rows into the blocks list (as synthetic "work" blocks) so every
// view that reads blocks via it sees timesheet data too.

function isoOfDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Timesheet rows store `date` as "DD-MM-YYYY" (see QuickAddModal's submit
// payload below); accept that or an already-ISO "YYYY-MM-DD" value.
function timesheetDateToIso(dateStr) {
  const parts = String(dateStr || "").split("-");
  if (parts.length !== 3) return "";
  const [a, b, c] = parts;
  return a.length === 4 ? `${a}-${b}-${c}` : `${c}-${b}-${a}`;
}

function useMergedBlocksFor() {
  const { blocksFor } = useCalendar();
  const { timesheets } = useTimesheets();
  const { attendanceData } = useAttendance();

  return (date, userId) => {
    const attendanceBlocks = blocksFor(date, userId);
    const iso = isoOfDate(date);
    const ddmmyyyy = formatDateDDMMYYYY(date);

    // The `/attendance-calendar` API can already include this same timesheet
    // entry as a "work" block in `attendanceBlocks` (e.g. once the backend
    // starts folding timesheet rows into the day's blocks itself). Without
    // this guard, the entry gets added a second time below from the separate
    // `/timesheets` list, so every timesheet appeared twice in the month
    // view and the sidebar's Timesheet Entries list. Skip any timesheet row
    // that already matches a "work" block (same start/end) the backend gave us.
    const hasWorkBlock = (start, end) => attendanceBlocks.some(
      (b) => b.type === "work" && b.start === start && b.end === end
    );

    const timesheetBlocks = (Array.isArray(timesheets) ? timesheets : [])
      .filter((t) => t && String(t.emp_id ?? t.user_id) === String(userId))
      .filter((t) => timesheetDateToIso(t.date) === iso)
      .filter((t) => t.check_in && t.check_out)
      .filter((t) => !hasWorkBlock(String(t.check_in).slice(0, 5), String(t.check_out).slice(0, 5)))
      .map((t) => ({
        type:    "work",
        label:   t.project_title || "Timesheet",
        sub:     `${to12h(String(t.check_in).slice(0, 5))} - ${to12h(String(t.check_out).slice(0, 5))}`,
        start:   String(t.check_in).slice(0, 5),
        end:     String(t.check_out).slice(0, 5),
        showEye: false,
        // Keep a reference to the raw timesheet record so the sidebar's
        // Timesheet Entries card can offer an edit action for this entry
        // (fixing a wrong date or wrong project) that posts back to the
        // exact source row via updateTimesheet(t.id, ...).
        raw: t,
      }));

    // The `/attendance-calendar` API (blocksFor above) marks weekend/off
    // days with an empty block list even when the employee actually
    // checked in that day — the real punch times only live in the separate
    // `/monthly-attendance` response (AttendanceContext), which is what the
    // Attendance page's table already reads successfully. Merge those real
    // punches in as synthetic checkin/checkout/break blocks (skipping any
    // punch type the calendar API already gave us) so a real weekend
    // check-in is never silently hidden.
    const times = (Array.isArray(attendanceData) ? attendanceData : [])
      .find((e) => e && String(e.user_id) === String(userId))
      ?.attendance_times?.[ddmmyyyy];

    const hasType = (type) => attendanceBlocks.some((b) => b.type === type);
    const punchBlocks = [];

    if (times?.check_in_time && !hasType("checkin")) {
      const start = times.check_in_time.slice(0, 5);
      const end   = times.check_out_time ? times.check_out_time.slice(0, 5) : start;
      punchBlocks.push({ type: "checkin", label: "Check-In", sub: to12h(start), start, end, showEye: true });
    }
    if (times?.check_out_time && !hasType("checkout")) {
      const end   = times.check_out_time.slice(0, 5);
      const start = times.check_in_time ? times.check_in_time.slice(0, 5) : end;
      punchBlocks.push({ type: "checkout", label: "Check-Out", sub: to12h(end), start, end, showEye: true });
    }
    if (times?.lunch_out_time && times?.lunch_in_time && !hasType("break")) {
      punchBlocks.push({
        type:  "break",
        label: "Break",
        sub:   `${to12h(times.lunch_out_time.slice(0, 5))} - ${to12h(times.lunch_in_time.slice(0, 5))}`,
        start: times.lunch_out_time.slice(0, 5),
        end:   times.lunch_in_time.slice(0, 5),
        showEye: false,
      });
    }

    return [...attendanceBlocks, ...punchBlocks, ...timesheetBlocks];
  };
}

// ---------------------------------------------------------------------------
// WEEK VIEW
// ---------------------------------------------------------------------------
// CHANGED: takes `userId` and reads blocks/status via CalendarContext
// (blocksFor/dayStatusFor/isWeekendDateFor) instead of the old mock-backed
// blocksFor(date)/dayStatusFor(date)/isWeekendDate(date) module functions.

function WeekView({ weekStart, selectedDate, setSelectedDate, onEyeClick, filters, userId }) {
  const { dayStatusFor, isWeekendDateFor } = useCalendar();
  const blocksFor = useMergedBlocksFor();
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const gridPx = GRID_HEIGHT;
  const weekGridRef = useRef(null);

  useEffect(() => {
    if (weekGridRef.current) weekGridRef.current.scrollTop = DEFAULT_SCROLL_HOUR * 60 * PX_PER_MIN;
  }, []);

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: 700 }}>
        {/* Day header row */}
        <div className="flex border-b border-gray-100">
          <div className="w-14 flex-shrink-0" />
          {days.map((d) => {
            const isToday    = isSameDay(d, new Date());
            const isSelected = isSameDay(d, selectedDate);
            // A weekend day with real check-in/timesheet blocks (e.g. work
            // logged on a Saturday) should not be treated as a blank weekend.
            const weekend    = isWeekendDateFor(d, userId) && blocksFor(d, userId).length === 0;
            const status     = dayStatusFor(d, userId);
            const isHoliday  = status === "holiday";
            const isLeave    = status === "leave";
            return (
              <button key={d.toISOString()} onClick={() => !weekend && !isHoliday && setSelectedDate(d)}
                className={`flex-1 text-center py-2 border-b-2 transition-colors ${
                  isToday ? "border-purple-600" : isSelected ? "border-purple-300" : "border-transparent hover:border-gray-200"
                }`}>
                <div className="text-[10px] uppercase tracking-wide text-gray-400">{DAY_LABELS[weekdayIndex(d)]}</div>
                <div className={`text-sm font-semibold mx-auto flex items-center justify-center ${
                  isToday ? "w-7 h-7 rounded-full bg-purple-600 text-white" : "text-gray-700"
                }`}>{d.getDate()}</div>
                {/* Only show the small status badge for non-holiday/non-leave
                    statuses (present/late/half). Holiday and Leave already
                    get their own dedicated block below in the grid body,
                    so showing the badge here too would duplicate the label. */}
                {!weekend && status && !isHoliday && !isLeave && (
                  <div className="mt-1 flex justify-center"><StatusBadge status={status} size="xs" /></div>
                )}
                {weekend && <div className="mt-1 text-[9px] text-green-600">Weekend</div>}
              </button>
            );
          })}
        </div>

        {/* Time grid — scrollable so overtime blocks outside 8am-8pm are reachable */}
        <div ref={weekGridRef} className="overflow-y-auto" style={{ maxHeight: VISIBLE_GRID_HEIGHT }}>
          <div className="flex" style={{ height: gridPx }}>
            <div className="w-14 flex-shrink-0 relative" style={{ height: gridPx }}>
              {HOURS.map((h, i) => (
                <span key={h} className="absolute right-2 text-[10px] text-gray-400 -translate-y-1/2 whitespace-nowrap"
                  style={{ top: i === 0 ? 10 : i * 60 * PX_PER_MIN, zIndex: 1 }}>
                  {hourLabel(h)}
                </span>
              ))}
            </div>

            {days.map((d) => {
              const rawBlocks  = blocksFor(d, userId);
              // Same rule as the header above: don't blank out a weekend day
              // that actually has check-in/timesheet blocks on it.
              const weekend    = isWeekendDateFor(d, userId) && rawBlocks.length === 0;
              const isSelected = isSameDay(d, selectedDate);
              const status     = dayStatusFor(d, userId);
              const isLeave    = status === "leave";
              const isHoliday  = status === "holiday";
              const blocks     = rawBlocks.filter(b => filters[b.type] !== false);

              return (
                <div key={d.toISOString()}
                  className={`flex-1 relative border-l border-gray-100 ${
                    weekend ? "bg-gray-50/60" : isSelected ? "bg-purple-50/30" : ""
                  }`}
                  style={{ height: gridPx }}>
                  <HourLines />

                  {weekend ? (
                    <div className="absolute inset-2 bg-green-50 border border-green-100 rounded-lg flex items-center justify-center text-xs text-green-600 gap-1">
                      Weekend
                    </div>
                  ) : isHoliday ? (
                    <div className="absolute inset-2 bg-red-50 border border-red-100 rounded-lg flex items-center justify-center text-xs text-red-600 gap-1">
                      <CalendarOff size={12} /> Holiday
                    </div>
                  ) : isLeave ? (
                    filters.leave !== false && (
                      <div className="absolute inset-2 bg-sky-50 border border-sky-100 rounded-lg flex items-center justify-center text-xs text-sky-600 gap-1">
                        <Palmtree size={12} /> On Leave
                      </div>
                    )
                  ) : (
                    blocks.map((block, idx) => {
                      const s = BLOCK_STYLES[block.type];
                      return (
                        <div key={idx}
                          onClick={block.showEye ? onEyeClick : undefined}
                          className="absolute rounded-md px-1.5 py-1"
                          style={{
                            left:       2,
                            right:      2,
                            top:        toTop(block.start),
                            height:     toHeight(block.start, block.end),
                            background: s.bg,
                            border:     `1px solid ${s.border}`,
                            color:      s.text,
                            fontSize:   10,
                            lineHeight: 1.3,
                            zIndex:     2,
                            cursor:     block.showEye ? "pointer" : "default",
                            overflow:   "visible",
                          }}>
                          <div className="flex items-center justify-between gap-1">
                            <span className="font-semibold truncate">{block.label}</span>
                            {block.showEye && <Eye size={10} />}
                          </div>
                          <div style={{ opacity: 1, fontSize: 9.5, fontWeight: 600 }}>{block.sub}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// DAY VIEW
// ---------------------------------------------------------------------------
// CHANGED: takes `userId`, reads blocks/status via CalendarContext.

function DayView({ selectedDate, onEyeClick, filters, userId }) {
  const { dayStatusFor, isWeekendDateFor } = useCalendar();
  const blocksFor = useMergedBlocksFor();
  const rawBlocks = blocksFor(selectedDate, userId);
  // Don't blank out a weekend day that actually has check-in/timesheet blocks.
  const weekend   = isWeekendDateFor(selectedDate, userId) && rawBlocks.length === 0;
  const status    = dayStatusFor(selectedDate, userId);
  const isLeave   = status === "leave";
  const isHoliday = status === "holiday";
  const blocks    = rawBlocks.filter(b => filters[b.type] !== false);
  const gridPx    = GRID_HEIGHT;
  const dayGridRef = useRef(null);

  useEffect(() => {
    if (dayGridRef.current) dayGridRef.current.scrollTop = DEFAULT_SCROLL_HOUR * 60 * PX_PER_MIN;
  }, [selectedDate]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-base font-semibold text-gray-800">
          {DAY_LABELS[weekdayIndex(selectedDate)]}, {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}
        </div>
        {/* Same rule as WeekView header: don't show the badge for holiday/leave,
            since the big centered panel below already communicates that. */}
        {!weekend && status && !isHoliday && !isLeave && <StatusBadge status={status} size="md" />}
      </div>

      {weekend ? (
        <div className="flex items-center justify-center h-40 text-gray-400 text-sm bg-green-50 rounded-xl border border-green-100">
          Weekend - no attendance data
        </div>
      ) : isHoliday ? (
        <div className="flex items-center justify-center h-40 text-red-600 text-sm bg-red-50 rounded-xl border border-red-100 gap-2">
          <CalendarOff size={16} /> Holiday - no attendance data
        </div>
      ) : isLeave ? (
        <div className="flex items-center justify-center h-40 text-sky-600 text-sm bg-sky-50 rounded-xl border border-sky-100 gap-2">
          <Palmtree size={16} /> On Leave - no attendance data
        </div>
      ) : (
        <div ref={dayGridRef} className="overflow-y-auto" style={{ maxHeight: VISIBLE_GRID_HEIGHT }}>
          <div className="flex" style={{ height: gridPx }}>
            <div className="w-14 flex-shrink-0 relative" style={{ height: gridPx }}>
              {HOURS.map((h, i) => (
                <span key={h} className="absolute right-2 text-[10px] text-gray-400 -translate-y-1/2 whitespace-nowrap"
                  style={{ top: i === 0 ? 10 : i * 60 * PX_PER_MIN, zIndex: 1 }}>
                  {hourLabel(h)}
                </span>
              ))}
            </div>
            <div className="flex-1 relative border-l border-gray-100" style={{ height: gridPx }}>
              <HourLines />
              {blocks.map((block, idx) => {
                const s = BLOCK_STYLES[block.type];
                return (
                  <div key={idx}
                    onClick={block.showEye ? onEyeClick : undefined}
                    className="absolute rounded-md px-2.5 py-2"
                    style={{
                      left:       4,
                      right:      4,
                      top:        toTop(block.start),
                      height:     toHeight(block.start, block.end),
                      background: s.bg,
                      border:     `1.5px solid ${s.border}`,
                      boxShadow:  "0 1px 3px rgba(0,0,0,0.1)", // separates stacked blocks visually
                      color:      s.text,
                      fontSize:   12,
                      lineHeight: 1.4,
                      zIndex:     3,
                      cursor:     block.showEye ? "pointer" : "default",
                      overflow:   "visible", // don't clip the sub-label on short blocks
                    }}>
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-bold truncate">{block.label}</span>
                      {block.showEye && <Eye size={12} />}
                    </div>
                    <div style={{ opacity: 1, fontSize: 11, fontWeight: 600, marginTop: 2 }}>{block.sub}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MONTH VIEW
// ---------------------------------------------------------------------------

const MAX_MONTH_CELL_TIMESHEET_ENTRIES = 3;

// CHANGED: takes `userId`, reads blocks via context instead of blocksFor(date).
function MonthCellPunches({ cellDate, filters, userId }) {
  const blocksFor = useMergedBlocksFor();
  const blocks        = blocksFor(cellDate, userId);
  const checkinBlock  = blocks.find(b => b.type === "checkin");
  const checkoutBlock = blocks.find(b => b.type === "checkout");
  const breakBlock    = blocks.find(b => b.type === "break");

  const rows = [
    checkinBlock  && filters.checkin  !== false && { label: "Check-In",  value: to12h(checkinBlock.start),  color: "#1e40af" },
    breakBlock    && filters.break    !== false && { label: "Lunch In",  value: to12h(breakBlock.start),    color: "#5b21b6" },
    breakBlock    && filters.break    !== false && { label: "Lunch Out", value: to12h(breakBlock.end),      color: "#5b21b6" },
    checkoutBlock && filters.checkin  !== false && { label: "Check-Out", value: to12h(checkoutBlock.start), color: "#166534" },
  ].filter(Boolean);

  const timesheetBlocks = blocks.filter(b => (b.type === "work" || b.type === "meeting") && filters[b.type] !== false);
  const visibleEntries  = timesheetBlocks.slice(0, MAX_MONTH_CELL_TIMESHEET_ENTRIES);
  const hiddenCount     = timesheetBlocks.length - visibleEntries.length;

  if (rows.length === 0 && timesheetBlocks.length === 0) return null;

  return (
    <div className="mt-1 space-y-[1px] hidden sm:block">
      {rows.map((r, i) => (
        <div key={`punch-${i}`} className="flex items-center justify-between gap-1 leading-tight" style={{ fontSize: 9 }}>
          <span className="text-gray-400 truncate">{r.label}</span>
          <span className="font-medium whitespace-nowrap" style={{ color: r.color }}>{r.value}</span>
        </div>
      ))}
      {visibleEntries.length > 0 && (
        <div className="mt-1 pt-1 border-t border-gray-100 space-y-[1px]">
          {visibleEntries.map((b, i) => (
            <div key={`ts-${i}`} className="truncate rounded px-1 leading-tight"
              style={{ fontSize: 9, background: b.type === "meeting" ? "#ffedd5" : "#fef3c7", color: b.type === "meeting" ? "#9a3412" : "#92400e" }}>
              {b.label}
            </div>
          ))}
          {hiddenCount > 0 && <div className="text-[9px] text-purple-600 font-medium px-1">+{hiddenCount} more</div>}
        </div>
      )}
    </div>
  );
}

// CHANGED: takes `userId`, passes it to MonthCellPunches and uses context
// for dayStatusFor/isWeekendDateFor instead of module-level mock functions.
//
// FIXED: Holiday (and Leave) were rendering TWICE — once as the small
// StatusBadge pill (top-right of the cell, since STATUS_META.holiday/leave
// already have their own label+icon) AND once as the dedicated red/sky
// "Holiday"/"On Leave" strip lower in the cell. We now suppress the small
// pill for holiday/leave days, since the dedicated strip already covers it
// (mirroring how weekends already skip the pill entirely).
function MonthView({ monthDate, selectedDate, setSelectedDate, filters, userId }) {
  const { dayStatusFor, isWeekendDateFor } = useCalendar();
  const blocksFor = useMergedBlocksFor();
  const year          = monthDate.getFullYear();
  const month         = monthDate.getMonth();
  const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;
  const totalDays     = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const today          = new Date();

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push({ day: prevMonthDays - firstDayOfWeek + 1 + i, current: false });
  for (let d = 1; d <= totalDays; d++) cells.push({ day: d, current: true });
  while (cells.length % 7 !== 0) cells.push({ day: cells.length - firstDayOfWeek - totalDays + 1, current: false });

  return (
    <div>
      <div className="text-base font-semibold text-gray-800 mb-4">{MONTH_NAMES[month]} {year}</div>
      <div className="grid grid-cols-7 border-b border-gray-100 mb-1">
        {DAY_LABELS.map(n => <div key={n} className="text-center text-[11px] font-medium text-gray-400 py-2 uppercase tracking-wide">{n}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          const cellDate   = cell.current ? new Date(year, month, cell.day) : null;
          const isToday    = cellDate && isSameDay(cellDate, today);
          const isSelected = cellDate && isSameDay(cellDate, selectedDate);
          // Don't blank out a weekend day that actually has check-in/timesheet blocks.
          const weekend    = cellDate
            ? (isWeekendDateFor(cellDate, userId) && blocksFor(cellDate, userId).length === 0)
            : (idx % 7 >= 5);
          const status     = cell.current && !weekend ? dayStatusFor(cellDate, userId) : null;
          const isLeave    = status === "leave";
          const isHoliday  = status === "holiday";
          // Only show the small pill/dot for statuses that don't already
          // get their own dedicated strip below (present / late / half).
          const showStatusPill = status && !isHoliday && !isLeave;
          return (
            <button key={idx} disabled={!cell.current || weekend || isHoliday}
              onClick={() => cellDate && setSelectedDate(cellDate)}
              className={`relative border border-gray-50 min-h-[60px] sm:min-h-[150px] p-1 sm:p-1.5 text-left transition-colors align-top
                ${!cell.current ? "opacity-30 cursor-default" : weekend || isHoliday ? "bg-gray-50 cursor-default" : "hover:bg-purple-50/40 cursor-pointer"}
                ${isSelected && cell.current && !weekend && !isHoliday ? "ring-2 ring-inset ring-purple-300" : ""}`}>
              <div className="flex items-center justify-between">
                <div className={`text-xs font-semibold w-6 h-6 flex items-center justify-center ${
                  isToday ? "rounded-full bg-purple-600 text-white" : "text-gray-700"
                }`}>{cell.day}</div>
                {showStatusPill && <span className="hidden sm:inline"><StatusBadge status={status} size="xs" /></span>}
                {showStatusPill && (
                  <span
                    className="sm:hidden w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: STATUS_META[status]?.color || "#9ca3af" }}
                  />
                )}
              </div>
              {weekend && cell.current && <div className="hidden sm:block text-[9px] text-green-600 mt-0.5">Off</div>}
              {isHoliday && <div className="hidden sm:flex text-[9px] text-red-600 mt-1 items-center gap-1"><CalendarOff size={9} /> Holiday</div>}
              {isLeave && <div className="hidden sm:flex text-[9px] text-sky-600 mt-1 items-center gap-1"><Palmtree size={9} /> On Leave</div>}
              {cell.current && !weekend && !isLeave && !isHoliday && <MonthCellPunches cellDate={cellDate} filters={filters} userId={userId} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TEAM VIEW
// ---------------------------------------------------------------------------
// CHANGED: reads `team` from CalendarContext instead of TEAM_MEMBERS mock.

function TeamView({ filter, onSelectMember }) {
  const { team, effectiveStatusFor  } = useCalendar();
  const resolvedTeam = useMemo(
    () => team.map((m) => ({
      ...m,
      status: effectiveStatusFor(m.id), // can be null before cutoff
    })),
    [team, effectiveStatusFor]
  );

  const filtered = filter === "all" ? resolvedTeam : resolvedTeam.filter(m => m.role === filter);
  const counts   = {
    present: filtered.filter(m => m.status === "present").length,
    late:    filtered.filter(m => m.status === "late").length,
    leave:   filtered.filter(m => m.status === "leave").length,
    half:    filtered.filter(m => m.status === "half").length,
    absent:  filtered.filter(m => m.status === "absent").length,
  };
  const avatarColors     = ["#ede9fe", "#dbeafe", "#dcfce7", "#fef3c7", "#ffedd5", "#fee2e2"];
  const avatarTextColors = ["#6d28d9", "#1e40af", "#166534", "#92400e", "#9a3412", "#991b1b"];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { label: "Present",  count: counts.present, status: "present" },
          { label: "Late",     count: counts.late,    status: "late"    },
          { label: "Leave",    count: counts.leave,   status: "leave"   },
          { label: "Half Day", count: counts.half,    status: "half"    },
        ].map(({ label, count, status }) => {
          const meta = STATUS_META[status];
          return (
            <div key={label} className="flex items-center gap-2 rounded-lg px-4 py-2" style={{ background: meta.bg }}>
              <meta.Icon size={15} style={{ color: meta.color }} strokeWidth={2.4} />
              <span className="text-lg font-semibold" style={{ color: meta.color }}>{count}</span>
              <span className="text-xs" style={{ color: meta.color }}>{label}</span>
            </div>
          );
        })}
      </div>
      <p className="hidden sm:block text-xs text-gray-400 mb-2">Click a team member to view their individual attendance calendar.</p>
      {filtered.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">No team members found for this filter.</div>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-2 px-3 text-xs font-medium text-gray-400">Member</th>
              <th className="text-left py-2 px-3 text-xs font-medium text-gray-400">Status</th>
              <th className="hidden sm:table-cell text-left py-2 px-3 text-xs font-medium text-gray-400">Check-In</th>
              <th className="hidden sm:table-cell text-left py-2 px-3 text-xs font-medium text-gray-400">Check-Out</th>
              <th className="text-left py-2 px-3 text-xs font-medium text-gray-400">Hours</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m, i) => {
              const ci = i % avatarColors.length;
              return (
                <tr key={m.id} onClick={() => onSelectMember(m)}
                  className="border-b border-gray-50 hover:bg-purple-50/50 transition-colors cursor-pointer">
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
                        style={{ background: avatarColors[ci], color: avatarTextColors[ci] }}>{initials(m.name)}</div>
                      <div>
                        <div className="text-gray-800 text-sm">{m.name}</div>
                        <div className="hidden sm:block text-xs text-gray-400">{m.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    {m.status ? <StatusBadge status={m.status} size="sm" /> : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="hidden sm:table-cell py-2.5 px-3 text-gray-700">{m.checkin}</td>
                  <td className="hidden sm:table-cell py-2.5 px-3 text-gray-700">{m.checkout}</td>
                  <td className="py-2.5 px-3 text-gray-800">{m.hours}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team View Dropdown
// ---------------------------------------------------------------------------

function TeamDropdown({ active, onSelect, teamFilter }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { team } = useCalendar();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const ROLE_ICONS = { Manager: Briefcase, Employee: Code, QA: Palette };
  const dynamicRoleOptions = useMemo(() => {
    const seen = new Map();
    (team || []).forEach((m) => {
      if (m.role && !seen.has(m.role)) {
        seen.set(m.role, {
          key: m.role,
          label: `${m.role} Team`,
          Icon: ROLE_ICONS[m.role] || Users,
        });
      }
    });
    return Array.from(seen.values());
  }, [team]);

  const options = [
    { key: "all", label: "All Members", Icon: Users },
    ...dynamicRoleOptions,
  ];
  const currentLabel = options.find(o => o.key === teamFilter)?.label || "Team View";

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen(o => !o); onSelect(teamFilter || "all"); }}
        className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm transition-colors max-w-[86px] sm:max-w-none ${
          active ? "bg-purple-600 text-white font-medium" : "text-gray-500 hover:text-gray-700"
        }`}>
        <span className="truncate">{active ? currentLabel : "Team View"}</span><ChevronDown size={12} className="flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 min-w-[170px] py-1">
          {options.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => { onSelect(key); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                teamFilter === key ? "text-purple-700 font-medium" : "text-gray-700"
              }`}>
              <Icon size={14} className={teamFilter === key ? "text-purple-600" : "text-gray-400"} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — Attendance Summary Card (unchanged — pure presentational)
// ---------------------------------------------------------------------------

function AttendanceSummaryCard({ dayData, selectedDateLabel, isSelectedToday }) {
  const headerStatus = dayData.weekend ? null : dayData.status;
  const headerMeta   = headerStatus ? STATUS_META[headerStatus] : null;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Attendance Summary</p>
          <p className="text-sm font-semibold text-purple-700 mt-0.5">
            {selectedDateLabel}{isSelectedToday ? " (Today)" : ""}
          </p>
        </div>
        {headerMeta && (
          <span className="flex items-center justify-center w-8 h-8 rounded-full flex-shrink-0" style={{ background: headerMeta.bg }}>
            <headerMeta.Icon size={16} style={{ color: headerMeta.color }} strokeWidth={2.4} />
          </span>
        )}
      </div>

      {dayData.weekend ? (
        <p className="text-sm text-gray-400 py-2">Weekend — no records.</p>
      ) : dayData.isHoliday ? (
        <p className="text-sm text-red-600 py-2 flex items-center gap-1.5"><CalendarOff size={14} /> Holiday — no records.</p>
      ) : dayData.isLeave ? (
        <p className="text-sm text-sky-600 py-2 flex items-center gap-1.5"><Palmtree size={14} /> On leave — no records.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {dayData.punches.map(({ key, label, value }) => (
            <div key={key} className="rounded-lg p-2.5" style={{ background: "#f8f7ff" }}>
              <p className="text-gray-400 flex items-center gap-1" style={{ fontSize: 10 }}>
                <Clock size={11} className="text-purple-400" />{label}
              </p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg p-3 text-center" style={{ background: "#f8f7ff" }}>
          <p className="text-xs text-gray-400">Half Day</p>
          <p className="text-sm font-semibold text-gray-800">{dayData.daySummary.halfDay}</p>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ background: "#f8f7ff" }}>
          <p className="text-xs text-gray-400">Efficiency</p>
          <p className="text-sm font-semibold text-green-600">{dayData.daySummary.efficiency}</p>
        </div>
        <div className="hidden sm:block rounded-lg p-3 text-center" style={{ background: "#f8f7ff" }}>
          <p className="text-xs text-gray-400">Productive</p>
          <p className="text-sm font-semibold text-gray-800">{dayData.daySummary.productive}</p>
        </div>
      </div>
      <div className="border-t border-gray-100 my-3" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Timesheet Entry Modal — fixes a single already-submitted timesheet
// entry (wrong employee, project, date, check-in/out timings, overtime or
// status) without touching anything else in the calendar/timesheet flow.
// ---------------------------------------------------------------------------

function calcWorkingHours(start, end) {
  if (!start || !end) return "";
  let diff = (new Date(`1970-01-01T${end}:00`) - new Date(`1970-01-01T${start}:00`)) / 60000;
  if (diff < 0) diff += 24 * 60;
  return `${Math.floor(diff / 60)}:${String(diff % 60).padStart(2, "0")}`;
}

function EditTimesheetEntryModal({ entry, onClose, currentUserId, isManager, userRole }) {
  const { employees } = useEmployee();
  const employeeList = Array.isArray(employees) ? employees : [];
  const { projects } = useProjects();
  const { updateTimesheet } = useTimesheets();

  const canSelectEmployee = isManager || userRole === "Admin" || userRole === "QA";

  // entry.date is stored as DD-MM-YYYY; <input type="date"> needs YYYY-MM-DD.
  const [empId,     setEmpId]     = useState(String(entry?.emp_id ?? entry?.empId ?? ""));
  const [date,      setDate]      = useState(entry?.date ? entry.date.split("-").reverse().join("-") : "");
  const [projectId, setProjectId] = useState(String(entry?.project_id ?? ""));
  const [checkIn,   setCheckIn]   = useState(entry?.check_in ? String(entry.check_in).slice(0, 5) : "");
  const [checkOut,  setCheckOut]  = useState(entry?.check_out ? String(entry.check_out).slice(0, 5) : "");
  const [overtime,  setOvertime]  = useState(entry?.overtime ?? "");
  const [status,    setStatus]    = useState(entry?.status || "Pending");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const workingHours = useMemo(() => calcWorkingHours(checkIn, checkOut), [checkIn, checkOut]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!empId || !projectId || !date || !checkIn || !checkOut) {
      setSubmitError("Please fill in all required fields.");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const payload = {
      emp_id: empId,
      project_id: projectId,
      date: date.split("-").reverse().join("-"), // YYYY-MM-DD -> DD-MM-YYYY
      check_in: checkIn,
      check_out: checkOut,
      working_hours: workingHours,
      overtime: overtime || "0",
      status: status || "Pending",
      update_audit_id: user?.id,
    };

    setSubmitting(true);
    setSubmitError("");
    try {
      await updateTimesheet(entry.id, payload);
      onClose();
    } catch (err) {
      setSubmitError(err.message || "Failed to update timesheet entry");
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls    = "block text-sm font-medium text-gray-700 mb-1";
  const inputCls    = "border border-gray-300 rounded-lg px-3 py-2 w-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white";
  const readonlyCls = "border border-gray-200 rounded-lg px-3 py-2 w-full text-sm bg-gray-50 text-gray-500 cursor-not-allowed";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[600px] relative overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">Edit Timesheet Entry</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {submitError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Employee Name</label>
              {!canSelectEmployee ? (
                <input
                  type="text"
                  readOnly
                  value={(() => {
                    const emp = employeeList.find(e => String(e.empId ?? e.id) === empId);
                    return emp ? `${emp.first_name ?? ""} ${emp.last_name ?? ""}`.trim() : "";
                  })()}
                  className={readonlyCls}
                />
              ) : (
                <div className="relative">
                  <select value={empId} onChange={e => setEmpId(e.target.value)} className={inputCls + " appearance-none pr-8"} required>
                    <option value="">Select Employee</option>
                    {employeeList
                      .filter(emp => emp.status?.toString().trim().toLowerCase() === "active" || String(emp.empId ?? emp.id) === empId)
                      .map(emp => (
                        <option key={emp.id} value={String(emp.empId || emp.id)}>
                          {emp.first_name} {emp.last_name}
                        </option>
                      ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              )}
            </div>
            <div>
              <label className={labelCls}>Project Name</label>
              <div className="relative">
                <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls + " appearance-none pr-8"} required>
                  <option value="">Select Project</option>
                  {Array.isArray(projects) && projects.map(p => (
                    <option key={p.id} value={String(p.id)}>{p.project_name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>Worked Hours</label>
              <input type="text" readOnly value={workingHours} placeholder="Auto-calculated" className={readonlyCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Start Time</label>
              <input type="time" value={checkIn} onChange={e => setCheckIn(e.target.value)} className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>End Time</label>
              <input type="time" value={checkOut} onChange={e => setCheckOut(e.target.value)} className={inputCls} required />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Overtime</label>
              <input type="text" value={overtime} onChange={e => setOvertime(e.target.value)} placeholder="e.g., 1.5 hrs" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <div className="relative">
                <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls + " appearance-none pr-8"}>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="border border-gray-300 px-5 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="text-white px-6 py-2 rounded-lg text-sm font-semibold shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#2563eb,#d12465)" }}>
              {submitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timesheet Entries Card
// ---------------------------------------------------------------------------
// CHANGED: entries backed by a real timesheet record (see `raw` set in
// deriveDayData / useMergedBlocksFor) now get a small edit (pencil) button
// so a wrong employee, project, date, timing, overtime or status on a
// single entry can be corrected in-place without touching anything else
// on the day.

function TimesheetEntriesCard({ dayData, isSelectedToday, selectedDateLabel, currentUserId, isManager, userRole }) {
  const entries = dayData.timesheet;
  const [editingEntry, setEditingEntry] = useState(null);

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
        Timesheet Entries ({isSelectedToday ? "Today" : selectedDateLabel})
      </p>
      {dayData.weekend ? (
        <p className="text-sm text-gray-400 py-2">Weekend — no timesheet entries.</p>
      ) : dayData.isHoliday ? (
        <p className="text-sm text-red-600 py-2 flex items-center gap-1.5"><CalendarOff size={14} /> Holiday — no timesheet entries.</p>
      ) : dayData.isLeave ? (
        <p className="text-sm text-sky-600 py-2 flex items-center gap-1.5"><Palmtree size={14} /> On leave — no timesheet entries.</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">No timesheet entries for this day.</p>
      ) : (
        entries.map(({ color, name, time, hrs, raw }, i) => (
          <div key={`${name}-${i}`} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
              <div>
                <p className="text-sm font-medium text-gray-800">{name}</p>
                <p className="text-xs text-gray-400">{time}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700">{hrs}</span>
              {raw && (
                <button
                  onClick={() => setEditingEntry(raw)}
                  title="Edit this timesheet entry"
                  className="p-1 rounded-md text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors flex-shrink-0"
                >
                  <Pencil size={13} />
                </button>
              )}
            </div>
          </div>
        ))
      )}

      {editingEntry && (
        <EditTimesheetEntryModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          currentUserId={currentUserId}
          isManager={isManager}
          userRole={userRole}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Personal Calendar
// ---------------------------------------------------------------------------
// CHANGED: computes dayData via context lookups for `person.id` instead of
// the global deriveDayData(date) mock helper. Month/week navigation that
// crosses into a different month now triggers fetchCalendar() for that
// month/year, since the backend only returns one month at a time.
//
// "Add Attendance" triggers the QR / manual-attendance / checkout-mail flow
// (via useAttendanceFlow), the same one used on the Attendance page,
// regardless of whose calendar is being viewed. It previously fell back to
// opening the "Add Timesheet" QuickAddModal when viewing a teammate's
// calendar (isOwnCalendar === false) — that modal creates a timesheet
// entry, not an attendance record, so "Add Attendance" was silently doing
// the wrong thing. QuickAddModal is still reachable via the separate
// "New Timesheet" button.

function PersonalCalendar({ person, onBack, isOwnCalendar }) {
  const { dayStatusFor, isWeekendDateFor, todayImagesFor, fetchCalendar, meta, graceInfoFor } = useCalendar();
  const blocksFor = useMergedBlocksFor();
  const today = new Date();

  const [selectedDate,    setSelectedDate]    = useState(today);
  const [anchorDate,      setAnchorDate]      = useState(today);
  const [activeView,      setActiveView]      = useState("week");
  const [showImageModal,  setShowImageModal]  = useState(false);
  const [showAddModal,    setShowAddModal]    = useState(false);
  const [filters,         setFilters]         = useState(Object.fromEntries(LEGEND.map(l => [l.key, true])));

  const attendanceFlow = useAttendanceFlow();

  const weekStart = useMemo(() => startOfWeek(anchorDate), [anchorDate]);

  // Build dayData for the selected date using live context data.
  const dayData = useMemo(() => {
    const status   = dayStatusFor(selectedDate, person.id);
    const weekend  = isWeekendDateFor(selectedDate, person.id);
    const blocks   = blocksFor(selectedDate, person.id);
    return deriveDayData(selectedDate, blocks, status, weekend);
  }, [selectedDate, person.id, blocksFor, dayStatusFor, isWeekendDateFor]);

  // Ensure the backend's loaded month matches whatever month is on screen.
  const ensureMonthLoaded = (date) => {
    if (!meta) return;
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    if (y !== meta.year || m !== meta.month) {
      fetchCalendar(y, String(m).padStart(2, "0"));
    }
  };

  const goToday = () => { setSelectedDate(today); setAnchorDate(today); ensureMonthLoaded(today); };
  const navPrev = () => {
    if (activeView === "day") {
      const d = addDays(selectedDate, -1); setSelectedDate(d); setAnchorDate(d); ensureMonthLoaded(d);
    } else if (activeView === "month") {
      const d = addMonths(anchorDate, -1); setAnchorDate(d); ensureMonthLoaded(d);
    } else {
      const d = addDays(anchorDate, -7); setAnchorDate(d); ensureMonthLoaded(d);
    }
  };
  const navNext = () => {
    if (activeView === "day") {
      const d = addDays(selectedDate, 1); setSelectedDate(d); setAnchorDate(d); ensureMonthLoaded(d);
    } else if (activeView === "month") {
      const d = addMonths(anchorDate, 1); setAnchorDate(d); ensureMonthLoaded(d);
    } else {
      const d = addDays(anchorDate, 7); setAnchorDate(d); ensureMonthLoaded(d);
    }
  };
  const rangeLabel = () => {
    if (activeView === "month") return `${MONTH_NAMES[anchorDate.getMonth()]} ${anchorDate.getFullYear()}`;
    if (activeView === "day")   return `${DAY_LABELS[weekdayIndex(selectedDate)]}, ${MONTH_NAMES[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`;
    return fmtRange(weekStart, addDays(weekStart, 6));
  };

  const isoDate           = (d) => d.toISOString().slice(0, 10);
  const selectedDateLabel = `${MONTH_NAMES[selectedDate.getMonth()].slice(0, 3)} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`;
  const isSelectedToday   = isSameDay(selectedDate, today);

  const handleAddAttendanceClick = () => {
    attendanceFlow.startAttendanceFlow(today.getMonth(), today.getFullYear());
  };

  return (
    <div>
      <div className="flex flex-col items-start sm:flex-row sm:items-center sm:justify-between mb-5 gap-3">
        <div className="flex items-center gap-3">
          {!isOwnCalendar && (
            <button onClick={onBack} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600 flex-shrink-0">
              <ArrowLeft size={16} />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
              style={{ background: "#ede9fe", color: "#6d28d9" }}>{initials(person.name)}</div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 leading-tight">{person.name}</h1>
              <p className="text-xs text-gray-400">
                {person.role}
                {!isOwnCalendar && <span className="hidden sm:inline"> · Individual attendance view</span>}
              </p>
            </div>
          </div>
        </div>
        <button onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
          style={{ background: "linear-gradient(135deg,#2563eb,#d12465)" }}>
          <Plus size={15} /> New Timesheet
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-3 mb-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={goToday} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50">Today</button>
          <button onClick={navPrev} className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50"><ChevronLeft size={16} /></button>
          <button onClick={navNext} className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50"><ChevronRight size={16} /></button>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 ml-1">{rangeLabel()}</span>
          <FiltersDropdown filters={filters} setFilters={setFilters} />
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {[{ label: "Day", key: "day" }, { label: "Week", key: "week" }, { label: "Month", key: "month" }].map(({ label, key }) => (
              <button key={key} onClick={() => setActiveView(key)}
                className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${activeView === key ? "bg-purple-600 text-white font-medium" : "text-gray-500 hover:text-gray-700"}`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => setShowImageModal(true)} title="View attendance images"
            className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-500 hover:text-gray-700 flex-shrink-0">
            <Eye size={16} />
          </button>
        </div>
      </div>

      <LegendBar filters={filters} setFilters={setFilters} onAddAttendance={handleAddAttendanceClick} />

      <div className="flex flex-col lg:flex-row gap-5">
        <div className="flex-1 bg-white rounded-xl shadow-md p-4 overflow-hidden">
          {activeView === "week"  && <WeekView weekStart={weekStart} selectedDate={selectedDate} setSelectedDate={(d) => { setSelectedDate(d); setAnchorDate(d); }} onEyeClick={() => setShowImageModal(true)} filters={filters} userId={person.id} />}
          {activeView === "day"   && <DayView  selectedDate={selectedDate} onEyeClick={() => setShowImageModal(true)} filters={filters} userId={person.id} />}
          {activeView === "month" && <MonthView monthDate={anchorDate} selectedDate={selectedDate} setSelectedDate={(d) => { setSelectedDate(d); setAnchorDate(d); }} filters={filters} userId={person.id} />}
        </div>

        <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-4">
          <AttendanceSummaryCard dayData={dayData} selectedDateLabel={selectedDateLabel} isSelectedToday={isSelectedToday} />
          <TimesheetEntriesCard
            dayData={dayData}
            isSelectedToday={isSelectedToday}
            selectedDateLabel={selectedDateLabel}
            currentUserId={meta?.current_user?.id}
            isManager={meta?.current_user?.role === "Manager"}
            userRole={meta?.current_user?.role}
          />
        </div>
      </div>

      {showImageModal && (
        <AttendanceModal
          onClose={() => setShowImageModal(false)}
          personName={person.name}
          todayImages={todayImagesFor(person.id)}
          graceInfo={graceInfoFor(person.id)}
        />
      )}
      {showAddModal && (
        <QuickAddModal
          onClose={() => setShowAddModal(false)}
          defaultDate={isoDate(selectedDate)}
          presetEmployeeId={person.id}
          currentUserId={meta?.current_user?.id}
          isManager={meta?.current_user?.role === "Manager"}
          userRole={meta?.current_user.role}
        />
      )}

      {/* QR + checkout-mail modals for "Add Attendance" */}
      <AttendanceFlowModals flow={attendanceFlow} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
// CHANGED: pulls currentUserId/team/meta from CalendarContext. Navigation
// that crosses a month boundary triggers fetchCalendar() for that month,
// since the API returns one month per request rather than a full mock year.
//
// "Add Attendance" now opens the same QR-code / checkout-mail flow used on
// the Attendance page (via useAttendanceFlow), instead of the manual
// QuickAddModal. "New Timesheet" still opens QuickAddModal.

export default function CalendarView() {
  const { currentUserId, meta, fetchCalendar, todayImagesFor, loading, error, graceInfoFor } = useCalendar();
  const today = new Date();

  const [selectedDate,   setSelectedDate]   = useState(today);
  const [anchorDate,     setAnchorDate]     = useState(today);
  const [activeView,     setActiveView]     = useState("week");
  const [teamFilter,     setTeamFilter]     = useState("all");

  const adminViewSet = useRef(false);

  useEffect(() => {
    if (
      !adminViewSet.current &&
      meta?.current_user?.role === "Admin"
    ) {
      adminViewSet.current = true;
      setActiveView("team");
      setTeamFilter("all");
    }
  }, [meta]);

  const [showImageModal, setShowImageModal] = useState(false);
  const [showAddModal,   setShowAddModal]   = useState(false);
  const [filters,        setFilters]        = useState(Object.fromEntries(LEGEND.map(l => [l.key, true])));
  const [viewingMember,  setViewingMember]  = useState(null);

  const { dayStatusFor, isWeekendDateFor } = useCalendar();
  const blocksFor = useMergedBlocksFor();

  const attendanceFlow = useAttendanceFlow();

  const weekStart = useMemo(() => startOfWeek(anchorDate), [anchorDate]);

  const dayData = useMemo(() => {
    if (!currentUserId) return deriveDayData(selectedDate, [], null, false);
    const status  = dayStatusFor(selectedDate, currentUserId);
    const weekend = isWeekendDateFor(selectedDate, currentUserId);
    const blocks  = blocksFor(selectedDate, currentUserId);
    return deriveDayData(selectedDate, blocks, status, weekend);
  }, [selectedDate, currentUserId, blocksFor, dayStatusFor, isWeekendDateFor]);

  const ensureMonthLoaded = (date) => {
    if (!meta) return;
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    if (y !== meta.year || m !== meta.month) {
      fetchCalendar(y, String(m).padStart(2, "0"));
    }
  };

  const handleTeamSelect = (filter) => { setTeamFilter(filter); setActiveView("team"); };
  const goToday = () => { setSelectedDate(today); setAnchorDate(today); ensureMonthLoaded(today); };
  const navPrev = () => {
    if (activeView === "day") {
      const d = addDays(selectedDate, -1); setSelectedDate(d); setAnchorDate(d); ensureMonthLoaded(d);
    } else if (activeView === "month") {
      const d = addMonths(anchorDate, -1); setAnchorDate(d); ensureMonthLoaded(d);
    } else {
      const d = addDays(anchorDate, -7); setAnchorDate(d); ensureMonthLoaded(d);
    }
  };
  const navNext = () => {
    if (activeView === "day") {
      const d = addDays(selectedDate, 1); setSelectedDate(d); setAnchorDate(d); ensureMonthLoaded(d);
    } else if (activeView === "month") {
      const d = addMonths(anchorDate, 1); setAnchorDate(d); ensureMonthLoaded(d);
    } else {
      const d = addDays(anchorDate, 7); setAnchorDate(d); ensureMonthLoaded(d);
    }
  };
  const rangeLabel = () => {
    if (activeView === "month") return `${MONTH_NAMES[anchorDate.getMonth()]} ${anchorDate.getFullYear()}`;
    if (activeView === "day")   return `${DAY_LABELS[weekdayIndex(selectedDate)]}, ${MONTH_NAMES[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`;
    return fmtRange(weekStart, addDays(weekStart, 6));
  };

  const isoDate           = (d) => d.toISOString().slice(0, 10);
  const selectedDateLabel = `${MONTH_NAMES[selectedDate.getMonth()].slice(0, 3)} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`;
  const isSelectedToday   = isSameDay(selectedDate, today);

  const handleAddAttendanceClick = () => {
    attendanceFlow.startAttendanceFlow(today.getMonth(), today.getFullYear());
  };

  if (viewingMember) {
    return (
      <main className="p-6 min-h-screen bg-[#f3f8ff] overflow-y-auto font-nunito">
        <PersonalCalendar person={viewingMember} onBack={() => setViewingMember(null)} isOwnCalendar={false} />
      </main>
    );
  }

  return (
    <main className="p-6 min-h-screen bg-[#f3f8ff] overflow-y-auto font-nunito">

      <div className="flex flex-col items-start sm:flex-row sm:items-center sm:justify-between mb-5 gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Attendance &amp; Timesheet Calendar</h1>
        <button onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm"
          style={{ background: "linear-gradient(135deg,#2563eb,#d12465)" }}>
          <Plus size={15} /> New Timesheet
        </button>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">{error}</div>
      )}
      {loading && (
        <div className="mb-3 text-sm text-gray-400">Loading attendance data...</div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-3 mb-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={goToday} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50">Today</button>
          {activeView !== "team" && <button onClick={navPrev} className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50"><ChevronLeft size={16} /></button>}
          {activeView !== "team" && <button onClick={navNext} className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50"><ChevronRight size={16} /></button>}
          {activeView !== "team" && <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 ml-1">{rangeLabel()}</span>}
          {activeView !== "team" && <FiltersDropdown filters={filters} setFilters={setFilters} />}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {[{ label: "Day", key: "day" }, { label: "Week", key: "week" }, { label: "Month", key: "month" }].map(({ label, key }) => (
              <button key={key} onClick={() => setActiveView(key)}
                className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors ${activeView === key ? "bg-purple-600 text-white font-medium" : "text-gray-500 hover:text-gray-700"}`}>
                {label}
              </button>
            ))}
            <TeamDropdown active={activeView === "team"} onSelect={handleTeamSelect} teamFilter={teamFilter} />
          </div>
          <button onClick={() => setShowImageModal(true)} title="View attendance images"
            className="p-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-500 hover:text-gray-700 flex-shrink-0">
            <Eye size={16} />
          </button>
        </div>
      </div>

      <LegendBar filters={filters} setFilters={setFilters} onAddAttendance={handleAddAttendanceClick} />

      <div className="flex flex-col lg:flex-row gap-5">
        <div className="flex-1 bg-white rounded-xl shadow-md p-4 overflow-hidden">
          {activeView === "week"  && <WeekView weekStart={weekStart} selectedDate={selectedDate} setSelectedDate={(d) => { setSelectedDate(d); setAnchorDate(d); }} onEyeClick={() => setShowImageModal(true)} filters={filters} userId={currentUserId} />}
          {activeView === "day"   && <DayView  selectedDate={selectedDate} onEyeClick={() => setShowImageModal(true)} filters={filters} userId={currentUserId} />}
          {activeView === "month" && <MonthView monthDate={anchorDate} selectedDate={selectedDate} setSelectedDate={(d) => { setSelectedDate(d); setAnchorDate(d); }} filters={filters} userId={currentUserId} />}
          {activeView === "team"  && <TeamView filter={teamFilter} onSelectMember={(m) => setViewingMember(m)} />}
        </div>

        {activeView !== "team" && (
          <div className="w-full lg:w-80 flex-shrink-0 flex flex-col gap-4">
            <AttendanceSummaryCard dayData={dayData} selectedDateLabel={selectedDateLabel} isSelectedToday={isSelectedToday} />
            <TimesheetEntriesCard
              dayData={dayData}
              isSelectedToday={isSelectedToday}
              selectedDateLabel={selectedDateLabel}
              currentUserId={currentUserId}
              isManager={meta?.current_user?.role === "Manager"}
              userRole={meta?.current_user?.role}
            />
          </div>
        )}
      </div>

      {showImageModal && <AttendanceModal onClose={() => setShowImageModal(false)} personName={meta ? undefined : undefined} todayImages={currentUserId ? todayImagesFor(currentUserId) : null} graceInfo={currentUserId ? graceInfoFor(currentUserId) : null} />}
      {showAddModal   && <QuickAddModal   onClose={() => setShowAddModal(false)} defaultDate={isoDate(selectedDate)} currentUserId={currentUserId} isManager={meta?.current_user?.role === "Manager"} userRole={meta?.current_user.role}/>}

      {/* QR + checkout-mail modals for "Add Attendance" */}
      <AttendanceFlowModals flow={attendanceFlow} />
    </main>
  );
}